import { useState } from 'react';
import { useSwapTutor } from '../../api/funnel';
import { Modal } from '../../components/Modal';
import { Spinner } from '../../components/Spinner';
import { TutorPicker } from '../../components/TutorPicker';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import type { AdminSubscription } from '../../types/api';

/**
 * Changing who teaches a child.
 *
 * Two things can move and the operator has to choose knowingly, so the
 * "also move the lessons already booked" switch is on screen rather than
 * assumed. Lessons the incoming tutor is busy for are reported back after
 * the swap instead of being silently left behind.
 *
 * Mounted with `key={subscription.id}` so each swap starts from a blank
 * form, rather than an effect that has to remember every field.
 */
export function SwapTutorModal({
  subscription,
  onClose,
}: {
  subscription: AdminSubscription;
  onClose: () => void;
}) {
  const swap = useSwapTutor();
  const toast = useToast();

  const [tutorId, setTutorId] = useState<string>();
  const [tutorName, setTutorName] = useState<string>();
  const [reason, setReason] = useState('');
  const [reassignScheduled, setReassignScheduled] = useState(true);
  const [leftBehind, setLeftBehind] = useState<string[]>([]);

  const submit = () => {
    if (!tutorId) return;
    swap.mutate(
      {
        id: subscription.id,
        tutorId,
        reason: reason.trim() || undefined,
        reassignScheduled,
      },
      {
        onSuccess: (result) => {
          toast.notify(
            `${subscription.student.fullName} is now with ${tutorName ?? 'the new tutor'}.`,
            'success',
          );
          // Close only when every lesson moved. If some did not, the
          // operator needs to see which ones before leaving this screen.
          if (result.lessonsLeftBehind.length === 0) onClose();
          else setLeftBehind(result.lessonsLeftBehind);
        },
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      width="lg"
      title="Swap tutor"
      description={`${subscription.student.fullName} · ${subscription.bundle.nameEn}`}
      footer={
        leftBehind.length > 0 ? (
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!tutorId || swap.isPending}
              onClick={submit}
            >
              {swap.isPending && <Spinner />}
              Swap the tutor
            </button>
          </>
        )
      }
    >
      {leftBehind.length > 0 ? (
        <div className="space-y-3">
          <p className="text-sm text-ink">
            The tutor was swapped, but {leftBehind.length}{' '}
            {leftBehind.length === 1 ? 'lesson' : 'lessons'} could not move —{' '}
            {tutorName ?? 'the new tutor'} is already booked then. Those lessons
            still belong to the previous tutor and need rescheduling by hand.
          </p>
          <ul className="space-y-1 rounded-md border border-rule bg-surface-2 px-3 py-2">
            {leftBehind.map((iso) => (
              <li key={iso} className="text-xs text-ink-2">
                {formatDateTime(iso)}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-ink-3">
            Currently with{' '}
            <span className="font-medium text-ink-2">
              {subscription.tutor?.fullName ?? 'nobody'}
            </span>
            . {subscription.lessonsScheduled} lesson
            {subscription.lessonsScheduled === 1 ? '' : 's'} already booked.
          </p>

          <TutorPicker
            value={tutorId}
            onChange={(id, name) => {
              setTutorId(id);
              setTutorName(name);
            }}
            excludeTutorId={subscription.tutor?.id}
            label="New tutor"
          />

          <label className="block">
            <span className="block text-xs font-medium text-ink-2">
              Reason{' '}
              <span className="font-normal text-ink-3">
                (for the audit log — the parent is not shown it)
              </span>
            </span>
            <input
              className="input mt-1"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Parent asked for a female teacher"
              maxLength={500}
            />
          </label>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={reassignScheduled}
              onChange={(e) => setReassignScheduled(e.target.checked)}
            />
            <span className="text-sm text-ink-2">
              Move the {subscription.lessonsScheduled} booked lesson
              {subscription.lessonsScheduled === 1 ? '' : 's'} to the new tutor
              <span className="mt-0.5 block text-xs text-ink-3">
                Unchecked, only lessons booked from now on use the new tutor.
              </span>
            </span>
          </label>

          {swap.isError && (
            <p role="alert" className="text-sm text-danger">
              {swap.error instanceof ApiError
                ? swap.error.message
                : 'Could not swap that tutor.'}
            </p>
          )}

          <p className="text-xs text-ink-3">
            The parent is notified that their tutor has been updated.
          </p>
        </div>
      )}
    </Modal>
  );
}
